import React from "react";
import { useAuth0 } from '../../contexts/useAuthContext';
import { userActivityLogout } from "../../services/admin/userActivity/activity.service";

const LoginBtn = () => {
    const authContext = useAuth0();

    const handleLogin = async () => {
        try {
            if (!authContext.loginWithRedirect) {
                console.info('loginWithRedirect method is not available!');
                return;
            }
            
            console.info('Calling loginWithRedirect...');
            await authContext.loginWithRedirect();
            console.info('loginWithRedirect completed');
        } catch (error) {
            console.info('Login failed with error:', error);
        }
      };

    return (
        <button 
            className="btn__login"
            title="Login"
            onClick={(e) => {
                console.info('Raw button click event:', e);
                e.preventDefault();
                handleLogin();
            }}
            type="button"
        >
            Login
        </button>
    );
}

const LogoutBtn = () => {
  const { logout } = useAuth0();

  const handleLogout = async () => {
    try {
      // Record logout activity (best-effort)
      try {
        await userActivityLogout();
      } catch (e) {
        // ignore activity logging errors
      }

      // Use the central logout handler which performs token revocation and OIDC signout
      await logout({ logoutParams: { returnTo: `${globalThis.location.origin}/login` } });
    } catch (error) {
      // Fallback local cleanup if logout fails
      try {
        globalThis.localStorage.clear();
        globalThis.sessionStorage.clear();
      } catch (e) {
        // ignore
      }
      globalThis.location.href = '/login';
    }
  };

  return (
    <button className="btn__logout" onClick={handleLogout}>
      Log Out
    </button>
  );
};

// Define the prop type
type SignUpBtnProps = {
    onSuccess: () => void; 
};

const SignUpBtn: React.FC<SignUpBtnProps> = ({ onSuccess }) => {
    const handleSignUpClick = () => {
        // Assume the sign-up is successful; handle logic here.
        // Call the onSuccess function to trigger the message display in the signup component.
        onSuccess();
    };

    return (
        <button
            className="btn__signin"
            title="Sign Up"
            onClick={handleSignUpClick}
        >
            Send Request
        </button>
    );
};

const GridViewBtn: React.FC<{ onClick: () => void; addClass: string }> = ({ onClick, addClass }) => {
  return (
    <button className={`btn__grid-view ${addClass}`} data-toggle="tooltip" title="Grid View" onClick={onClick}>
    </button>
  );
};

const ListViewBtn: React.FC<{ onClick: () => void; addClass: string }> = ({ onClick, addClass }) => {
  return (
    <button className={`btn__list-view ${addClass}`} data-toggle="tooltip" title="List View" onClick={onClick}>
    </button>
  );
};

interface SettingsBtnProps {
  onOpen: () => void;
}

const SettingsBtn: React.FC<SettingsBtnProps> = ({ onOpen }) => {
  const { userProfile, isAuthenticated } = useAuth0();

  return (
      <>
          {isAuthenticated && userProfile ? (
              <button className="btn__settings" onClick={onOpen}>
              </button>
          ) : null}
      </>
  );
}

interface CloseBtnProps {
  onClick: () => void;
}

const CloseBtn: React.FC<CloseBtnProps> = ({ onClick }) => {
return (
    <button className="btn__close se-icon--close" onClick={onClick}>
    </button>
  );
}

interface FilterBtnProps {
  onClick?: () => void;
  isActive?: boolean;
  isDropdownOpen?: boolean;
}

const FilterBtn = React.forwardRef<HTMLButtonElement, FilterBtnProps>(({ onClick, isActive = false, isDropdownOpen = false }, ref) => {
  const getClassName = () => {
    let className = "btn__filter";
    if (isActive) className += " filter-active";
    if (isDropdownOpen) className += " dropdown-open";
    return className;
  };

  return (
    <button
        title="Filter"
        ref={ref}
        className={getClassName()}
        onClick={onClick}
    >
        Filter
    </button>
  );
});

FilterBtn.displayName = 'FilterBtn';

const FavouritesBtn  = () => {
  return (
    <button
        title="Favourites"
        className="btn__favourites"
    >
        Favourites
    </button>
  );
}

const SortBtn  = () => {
  return (
    <button
        title="Sort"
        className="btn__sort"
    >
        Sort
    </button>
  );
}

// Reusable actions toggle button used in table rows
const ActionsToggle = React.forwardRef<HTMLButtonElement, {
  isExpanded?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  ariaControls?: string;
  className?: string;
  disabled?: boolean;
}>(({ isExpanded = false, onClick, ariaControls, className, disabled = false }, ref) => {
  return (
    <button
      ref={ref}
      className={className ?? 'actions__toggle'}
      onClick={onClick}
      type="button"
      aria-haspopup="true"
      aria-expanded={isExpanded}
      aria-controls={ariaControls}
      disabled={disabled}
    ></button>
  );
});

ActionsToggle.displayName = 'ActionsToggle';

const CancelBtn: React.FC<{ onCancel: () => void; isLoading?: boolean }> = ({ onCancel, isLoading = false }) => (
  <button className="btn-cancel" onClick={onCancel} disabled={isLoading}>
    Cancel {isLoading && <span className="loader ms-1"></span>}
  </button>
);

const OkBtn: React.FC<{ onOk: () => void; isLoading?: boolean }> = ({ onOk, isLoading = false }) => (
  <button className="btn-ok" onClick={onOk} disabled={isLoading}>
    Ok {isLoading && <span className="loader ms-1"></span>}
  </button>
);

// Reusable sliding switch for notifications
interface SlidingSwitchButtonProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel?: string;
}

const SlidingSwitchButton: React.FC<SlidingSwitchButtonProps> = ({ checked, disabled, onChange, ariaLabel }) => (
  <label
    className={
      [
        'sliding-switch',
        checked ? 'sliding-switch--checked' : '',
        disabled ? 'sliding-switch--disabled' : ''
      ].filter(Boolean).join(' ')
    }
  >
    <input
      type="checkbox"
      className="sliding-switch__input"
      checked={checked}
      disabled={disabled}
      onChange={e => onChange(e.target.checked)}
      aria-label={ariaLabel || 'Toggle notifications'}
    />
    <span className="sliding-switch__track">
      <span className="sliding-switch__thumb" />
    </span>
  </label>
);

export {  LoginBtn, 
          LogoutBtn, 
          SignUpBtn, 
          GridViewBtn, 
          ListViewBtn, 
          SettingsBtn, 
          CloseBtn, 
          FilterBtn, 
          FavouritesBtn, 
          SortBtn,
          ActionsToggle,
          CancelBtn,
          OkBtn,
          SlidingSwitchButton
        };